import { useUserPermissions } from './useUserPermissions';
import { PermissionKey } from '@/types/roles';

/**
 * Check if the current user has a specific permission.
 * 
 * FAIL-CLOSED BEHAVIOR:
 * - Returns `false` while loading
 * - Returns `false` on error
 * - Returns `false` if user has no role
 * 
 * This ensures security over convenience.
 */
export function useHasPermission(permissionKey: PermissionKey): boolean {
  const { permissions, isLoading, error } = useUserPermissions();
  
  // Fail-closed: deny access if loading, error, or no permissions
  if (isLoading || error || !permissions) {
    return false;
  }
  
  return permissions.includes(permissionKey);
}

/**
 * Check if the current user has ALL of the specified permissions.
 */
export function useHasAllPermissions(permissionKeys: PermissionKey[]): boolean {
  const { permissions, isLoading, error } = useUserPermissions();
  
  if (isLoading || error || !permissions) {
    return false;
  }
  
  return permissionKeys.every(key => permissions.includes(key));
}

/**
 * Check if the current user has ANY of the specified permissions.
 */
export function useHasAnyPermission(permissionKeys: PermissionKey[]): boolean {
  const { permissions, isLoading, error } = useUserPermissions();
  
  if (isLoading || error || !permissions) {
    return false;
  }
  
  return permissionKeys.some(key => permissions.includes(key));
}
